import React, { useState, useEffect, useRef } from "react";

const CardSlider = ({
  cards = [],
  autoplayInterval = 4000,
  showArrows = true,
  showDots = true,
  className = "",
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const sliderRef = useRef(null);
  const autoplayRef = useRef(null);

  // Auto-play functionality
  useEffect(() => {
    if (autoplayInterval > 0) {
      autoplayRef.current = setInterval(() => {
        nextSlide();
      }, autoplayInterval);
    }

    return () => {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
      }
    };
  }, [autoplayInterval]);

  // Pause autoplay on hover
  const handleMouseEnter = () => {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
    }
  };

  const handleMouseLeave = () => {
    if (autoplayInterval > 0) {
      autoplayRef.current = setInterval(() => {
        nextSlide();
      }, autoplayInterval);
    }
  };

  const nextSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev + 1) % cards.length);
    setTimeout(() => setIsTransitioning(false), 700);
  };

  const prevSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev - 1 + cards.length) % cards.length);
    setTimeout(() => setIsTransitioning(false), 700);
  };

  const goToSlide = (index) => {
    if (isTransitioning || index === currentIndex) return;
    setIsTransitioning(true);
    setCurrentIndex(index);
    setTimeout(() => setIsTransitioning(false), 700);
  };

  // Get visible cards with their positions
  const getVisibleCards = () => {
    const visibleCards = [];
    const totalCards = cards.length;

    if (totalCards === 0) return visibleCards;

    // Always show center card
    visibleCards.push({
      ...cards[currentIndex],
      position: "center",
      index: currentIndex,
    });

    // Add left cards
    for (let i = 1; i <= 2; i++) {
      const leftIndex = (currentIndex - i + totalCards) % totalCards;
      if (leftIndex !== currentIndex) {
        visibleCards.push({
          ...cards[leftIndex],
          position: "left",
          index: leftIndex,
          offset: i,
        });
      }
    }

    // Add right cards
    for (let i = 1; i <= 2; i++) {
      const rightIndex = (currentIndex + i) % totalCards;
      if (rightIndex !== currentIndex) {
        visibleCards.push({
          ...cards[rightIndex],
          position: "right",
          index: rightIndex,
          offset: i,
        });
      }
    }

    return visibleCards;
  };

  const visibleCards = getVisibleCards();

  if (cards.length === 0) {
    return (
      <div className={`flex items-center justify-center h-96 ${className}`}>
        <p className="text-gray-500">No cards to display</p>
      </div>
    );
  }

  return (
    <div
      className={`relative w-full max-w-7xl mx-auto px-4 md:px-8 ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Slider Container */}
      <div className="relative h-[450px] md:h-[500px] overflow-visible">
        {/* Cards */}
        <div className="relative w-full">
          {visibleCards.map((card, index) => {
            const isCenter = card.position === "center";
            const isLeft = card.position === "left";
            const isRight = card.position === "right";

            let transform = "";
            let opacity = 1;
            let zIndex = 10;
            let scale = 1;

            if (isCenter) {
              transform = "translateX(-50%)";
              zIndex = 30;
              scale = 1;
            } else if (isLeft) {
              const offset = card.offset || 1;
              transform = `translateX(${-50 - offset * 25}%) translateY(${
                offset * 8
              }px) rotateY(${offset * 8}deg)`;
              opacity = 1 - offset * 0.7;
              zIndex = 20 - offset;
              scale = 1 - offset * 0.05;
            } else if (isRight) {
              const offset = card.offset || 1;
              transform = `translateX(${-50 + offset * 25}%) translateY(${
                offset * 8
              }px) rotateY(${-offset * 8}deg)`;
              opacity = 1 - offset * 0.7;
              zIndex = 20 - offset;
              scale = 1 - offset * 0.05;
            }

            return (
              <div
                key={`${card.index}-${card.position}`}
                className={`absolute top-1/2 left-1/2 w-80 sm:w-72 md:w-80 lg:w-96 transition-all duration-700 ease-in-out ${
                  isTransitioning
                    ? "pointer-events-none"
                    : "pointer-events-auto"
                }`}
                style={{
                  transform,
                  opacity,
                  zIndex,
                  scale,
                }}
              >
                <div
                  className={`bg-white rounded-3xl shadow-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-3xl h-[500px] flex flex-col ${
                    isCenter ? "hover:scale-105" : "hover:scale-110"
                  }`}
                  onClick={() => {
                    if (card.link) {
                      window.open(card.link, "_blank", "noopener,noreferrer");
                    } else if (!isCenter) {
                      goToSlide(card.index);
                    }
                  }}
                >
                  {/* Card Image */}
                  <div className="relative h-44 md:h-64 overflow-hidden">
                    <img
                      src={card.image || card.logo}
                      alt={card.title || card.name}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>

                    {/* Card Badge */}
                    {card.badge && (
                      <div className="absolute top-4 right-4">
                        <span className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                          {card.badge}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Card Content */}
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 line-clamp-2">
                      {card.title || card.name}
                    </h3>
                    <p className="text-sm text-gray-600 my-5 line-clamp-3 flex-1">
                      {card.description || card.bio}
                    </p>

                    {/* Card Footer */}
                    <div className="flex items-center justify-between mt-auto">
                      <div className="flex items-center space-x-2">
                        {card.country && (
                          <span className="text-xs text-gray-500">
                            {card.country}
                          </span>
                        )}
                        {card.category && (
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">
                            {card.category}
                          </span>
                        )}
                      </div>

                      {card.link && (
                        <a
                          href={card.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-emerald-600 hover:text-emerald-700 transition-colors duration-300"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            window.open(
                              card.link,
                              "_blank",
                              "noopener,noreferrer"
                            );
                          }}
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                            />
                          </svg>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Navigation Arrows */}
        {showArrows && cards.length > 1 && (
          <>
            <button
              onClick={prevSlide}
              disabled={isTransitioning}
              className="absolute sm:left-4 left-[-5px] top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed z-40"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <button
              onClick={nextSlide}
              disabled={isTransitioning}
              className="absolute sm:right-4 right-[-5px] top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed z-40"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </>
        )}
      </div>

      {/* Dots Indicator */}
      {showDots && cards.length > 1 && (
        <div className="hidden sm:flex justify-center space-x-2 mt-8">
          {cards.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              disabled={isTransitioning}
              className={`w-3 h-3 rounded-full transition-all duration-300 disabled:cursor-not-allowed ${
                index === currentIndex
                  ? "bg-emerald-600 scale-125"
                  : "bg-gray-300 hover:bg-emerald-400"
              }`}
            />
          ))}
        </div>
      )}

      {/* Slide Counter */}
      <div className="absolute bottom-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm z-40">
        {currentIndex + 1} / {cards.length}
      </div>
    </div>
  );
};

export default CardSlider;
